import { jsx as _jsx } from "react/jsx-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import { AuthContext } from 'libs.react_internal';
import { getApiInstance } from '../../helpers/api.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { CortexSessionHandlerContext } from '../cortex_session_handler/cortex_session_handler.context';
export const AxonAccountContext = createContext({
    isLoading: true,
    account: null,
});
export const AxonAccountProvider = ({ children }) => {
    const queryClient = useQueryClient();
    const match = useMatch('/site/study/:studyId/*');
    const { user } = useContext(AuthContext);
    const { cortexSessionData } = useContext(CortexSessionHandlerContext);
    const { environment } = useCurrentEnvironment(match?.params?.studyId);
    const getAxonAccount = async () => {
        if (!environment) {
            return;
        }
        if (cortexSessionData) {
            return cortexSessionData;
        }
        const { data: response } = await getApiInstance(environment).get(`/v2/hub/accounts/login`);
        return response || null;
    };
    const accountQueryKey = useMemo(() => ['getAxonAccount', environment?.id?.toString(), user?.id.toString(), environment?.cortexURL], [environment?.cortexURL, environment?.id, user?.id]);
    const { data: account, isLoading } = useQuery({
        queryKey: accountQueryKey,
        queryFn: getAxonAccount,
        enabled: !!environment && !!user?.id,
        // A lane switch only rewrites cortexURL, and every lane serves the same environment account, so the
        // prior account stays valid while the new lane refetches. A different environment or user issues a
        // different account id, so the placeholder must be dropped there rather than leaking across studies.
        placeholderData: (previousData, previousQuery) => {
            const [, previousEnvironmentId, previousUserId] = previousQuery?.queryKey ?? [];
            const [, environmentId, userId] = accountQueryKey;
            return previousEnvironmentId === environmentId && previousUserId === userId ? previousData : undefined;
        },
    });
    const setAccount = useCallback((updatedAccount) => queryClient.setQueryData(accountQueryKey, updatedAccount), [accountQueryKey, queryClient]);
    const value = useMemo(() => ({
        isLoading,
        account,
        setAccount,
    }), [account, isLoading, setAccount]);
    return _jsx(AxonAccountContext.Provider, { value: value, children: children });
};
